import { createInitialState, positionKey } from "./board";
import { isInsufficientMaterial } from "./material";
import { allLegalMoves, applyMove, isInCheck, legalMovesFrom } from "./moves";
import { moveToSanBase } from "./notation";
import type { Color, GameState, GameStatus, HistoryEntry, Move, PieceType, Square } from "./types";

export type PromotionPiece = Exclude<PieceType, "p" | "k">;

/** Machine a etats pure (sans DOM) : deplacements legaux, application d'un coup,
 * detection echec/mat/pat/nulles, historique en notation algebrique. */
export class ChessEngine {
  private state: GameState;
  private history: HistoryEntry[] = [];
  private positionCounts = new Map<string, number>();

  constructor(initialState: GameState = createInitialState()) {
    this.state = initialState;
    this.positionCounts.set(positionKey(this.state), 1);
  }

  getState(): GameState {
    return this.state;
  }

  get turn(): Color {
    return this.state.turn;
  }

  legalMovesFrom(square: Square): Move[] {
    return legalMovesFrom(this.state, square);
  }

  allLegalMoves(): Move[] {
    return allLegalMoves(this.state);
  }

  isInCheck(color: Color = this.state.turn): boolean {
    return isInCheck(this.state, color);
  }

  getStatus(): GameStatus {
    const moves = this.allLegalMoves();
    const inCheck = this.isInCheck();
    if (moves.length === 0) return inCheck ? "checkmate" : "stalemate";
    if (this.state.halfmoveClock >= 100) return "draw-fifty-moves";
    if ((this.positionCounts.get(positionKey(this.state)) ?? 0) >= 3) return "draw-repetition";
    if (isInsufficientMaterial(this.state.board)) return "draw-insufficient-material";
    return inCheck ? "check" : "playing";
  }

  isGameOver(): boolean {
    const status = this.getStatus();
    return status !== "playing" && status !== "check";
  }

  /** Tente de jouer un coup legal. Retourne l'entree d'historique si applique, null sinon. */
  move(from: Square, to: Square, promotion?: PromotionPiece): HistoryEntry | null {
    const candidates = this.legalMovesFrom(from);
    const chosen = candidates.find((m) => m.to === to && (m.promotion === undefined || m.promotion === promotion));
    if (!chosen) return null;

    const legalInPosition = this.allLegalMoves();
    const stateBefore = this.state;
    const stateAfter = applyMove(this.state, chosen);

    let san = moveToSanBase(chosen, legalInPosition);
    const opponentInCheck = isInCheck(stateAfter, stateAfter.turn);
    const opponentHasMoves = allLegalMoves(stateAfter).length > 0;
    if (opponentInCheck) san += opponentHasMoves ? "+" : "#";

    this.state = stateAfter;
    const key = positionKey(stateAfter);
    this.positionCounts.set(key, (this.positionCounts.get(key) ?? 0) + 1);

    const entry: HistoryEntry = { move: chosen, san, stateBefore, stateAfter };
    this.history.push(entry);
    return entry;
  }

  getHistory(): readonly HistoryEntry[] {
    return this.history;
  }
}
